import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { makeBookingManagerApi } from 'services/BookingManagerApi/BookingManagerApi';
import { useNotifications } from 'hooks/useNotifications';
import { IBookingComment, IBookingComments } from 'services/BookingManagerApi/types/BookingManagerComments';
import { IBankTransactionComment } from '../types';

// Query key constant for bank transaction comments
export const BANK_TRANSACTION_COMMENTS_QUERY_KEY = 'bankTransactionComments';

/**
 * Hook for retrieving comments for a bank transaction.
 * 
 * @param transactionUuid - The UUID of the bank transaction
 * @param enabled - Whether the query should be enabled (defaults to true if transactionUuid is provided)
 * @returns Query result with comments data
 */
export const useBankTransactionComments = (
  transactionUuid: string | null | undefined,
  enabled: boolean = true
) => {
  return useQuery<IBankTransactionComment[]>({
    queryKey: [BANK_TRANSACTION_COMMENTS_QUERY_KEY, transactionUuid],
    queryFn: async () => {
      if (!transactionUuid) {
        throw new Error('Transaction UUID is required');
      }
      const api = makeBookingManagerApi();
      const response = await api.getCommentsForBankTransaction(transactionUuid);
      return response.data;
    },
    enabled: !!transactionUuid && enabled,
  });
};

/**
 * Hook for posting a comment to a bank transaction.
 * 
 * @returns Mutation object with mutate function and status
 */
export const usePostBankTransactionComment = (showSuccessNotification: (message: string) => void, showErrorNotification: (message: string) => void) => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<IBookingComment>,
    Error,
    { transactionUuid: string; commentText: string; }
  >({
    mutationKey: ['postBankTransactionComment'],
    mutationFn: async ({ transactionUuid, commentText }) => {
      const api = makeBookingManagerApi();
      return await api.addCommentToBankTransaction(transactionUuid, commentText);
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch comments after successful post
      queryClient.invalidateQueries({
        queryKey: [BANK_TRANSACTION_COMMENTS_QUERY_KEY, variables.transactionUuid],
      });
      showSuccessNotification('Comment added successfully');
    },
    onError: (error) => {
      showErrorNotification('Unable to save comment');
    },
  });
};

/**
 * Hook for deleting a comment from a bank transaction.
 * 
 * @returns Mutation object with mutate function and status
 */
export const useDeleteBankTransactionComment = (showSuccessNotification: (message: string) => void, showErrorNotification: (message: string) => void) => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<void>,
    Error,
    { transactionUuid: string; commentUuid: string; }
  >({
    mutationKey: ['deleteBankTransactionComment'],
    mutationFn: async ({ transactionUuid, commentUuid }) => {
      const api = makeBookingManagerApi();
      return await api.deleteCommentFromBankTransaction(transactionUuid, commentUuid);
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch comments after successful deletion
      queryClient.invalidateQueries({
        queryKey: [BANK_TRANSACTION_COMMENTS_QUERY_KEY, variables.transactionUuid],
      });
      showSuccessNotification('Comment deleted successfully');
    },
    onError: (error) => {
      showErrorNotification(error.message);
    },
  });
};

